import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isNull, isUndefined, isEqual } from "lodash";

import locationService from "@plannel/services/data/location-service"
import workcenterService from "@plannel/services/data/workcenter-service";
import resourceService from "@plannel/services/data/resource-service";
import calendarGroupService from "@plannel/services/data/calendar-group-service";
import CalendarGroupRenderer from "@plannel/components/aggrid/CalendarGroupRenderer";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting, { gridPaste, nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import DateUtils from "@plannel/utils/date-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;
const { timeColumnDecimalLimit } = CodeUtil;

const Resource = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? null);
  const [resourceSearch, setResourceSearch] = useState(reduxViewState?.resourceSearch ?? null);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [workcenters, setWorkcenters] = useState(reduxViewState?.workcenters ?? []);
  const [productionResources, setProductionResources] = useState([]);
  const [locations, setLocations] = useState(reduxViewState?.locations ?? []);
  const [calendarGroups, setCalendarGroups] = useState(reduxViewState?.calendarGroups ?? []);
  const workcenterMap= useRef({});

  // WeekDay enum values from codeUtil with safety checks
  const weekDays = useMemo(() => {
    // Default WeekDay values as fallback (matching WeekDay enum order)
    const defaultWeekDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    
    if (!CodeUtil?.dayOfWeek || isEmpty(CodeUtil.dayOfWeek)) {
      return defaultWeekDays;
    }
    
    const extractedDays = Object.values(CodeUtil.dayOfWeek)
      .filter(day => day && day.cd) // Filter out null/undefined entries
      .map(day => day.cd);
    
    // Return default if extraction failed or resulted in empty array
    return extractedDays.length > 0 ? extractedDays : defaultWeekDays;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valueFormatter = (param) => {
    if (param.column?.colDef) {
      const { colId } = param.column.getColDef();
      if (colId === "nopCalGrp") {
        return param.value?.name ? param.value.name : t("notConfigured");
      }
    }

    return param.value?.name ? param.value.name : "";
  }

  const dropdownResourceValueFormatter = (data) => {
    if (typeof data?.value === "string") {
      return data.value;
    }
    return data.value ? data.value.wcCd : "";
  }

  const workStartDayValueFormatter = (data) => {
    if (typeof data?.value === "string") {
      return t(data.value);
    }
    return data.value ? t(data.value) : "";
  }

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    
    return nullSafeComparator(val1, val2, isDescending);
  }

  // Suppress delete/backspace/cut keys for workStartDay column
  const suppressWorkStartDayKeyDelete = (params) => {
    const { event, column } = params;
    const colId = column.getColId();
    
    if (colId === 'workStartDay') {
      const key = event.key;
      // Suppress Delete, Backspace keys
      if (key === 'Delete' || key === 'Backspace') {
        return true;
      }
      // Suppress Ctrl+X (Cut)
      if ((event.ctrlKey || event.metaKey) && key.toLowerCase() === 'x') {
        return true;
      }
    }
    return false;
  }

  // Validate workStartDay value on cell value change
  const validateWorkStartDayValue = (params) => {
    const { colDef, newValue, data } = params;
    
    if (colDef.colId === 'workStartDay') {
      // Check if the pasted/entered value is valid
      if (newValue && typeof newValue === 'string') {
        const normalizedValue = newValue.toLowerCase().trim();
        
        // Check if value is in weekDays array
        if (!weekDays.includes(normalizedValue)) {
          setSnackInfo({ 
            open: true, 
            severity: snackBarMessageColor.inputError, 
            content: `${t("MSG_InvalidValue")} [${t("workStartDay")}]: ${normalizedValue}` 
          });
          // Revert to previous value or default
          data.workStartDay = data.workStartDay || 'mon';
          return false;
        }
        // Set normalized value
        data.workStartDay = normalizedValue;
      }
    }
    return true;
  }

  const onCellValueChanged = (params) => {
    gridPaste(params);

    const { column, node } = params;
    const colId = column.getColId();
    
    // Validate workStartDay value
    if (colId === 'workStartDay') {
      if (!validateWorkStartDayValue(params)) {
        // Refresh the cell to show the reverted value
        params.api.refreshCells({ rowNodes: [node], columns: [column], force: true });
        return;
      }
    }
    
    if (isEqual(colId, "workcenter")) {
      node.data.wcCd = workcenterMap.current[params.value]?.wcCd;
      node.data.locCd = workcenterMap.current[params.value]?.locationCd;
      node.data.locName = workcenterMap.current[params.value]?.locationName;
    } 
    DataState.setDataState(params);
  }

  const checkInputValidation = (params) => {
    const colId = params.column.colId;

    if (isNaN(params.newValue) || isNull(params.newValue) || isUndefined(params.newValue)) {
      params.data[colId] = null;

      return true;
    }

    if (params.newValue < 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_InvalidValue") });
      return false;
    }

    const decimalPart = params.newValue.toString().split('.')[1];

    if (decimalPart && decimalPart.length > timeColumnDecimalLimit) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_LimitDecimalPoint", { N: timeColumnDecimalLimit }) });
      return false;
    }

    params.data[colId] = params.newValue;

    return true;
  }

  const columnDefs = [
    {
      headerName: "locCd", field: "locCd", colId: "locationCd",
      type: ['nonEditableColumn'],
      cellClass: 'stringType',
      width: 200, filterType: 'string'
    },
    {
      headerName: "locName", field: "locName", colId: "locationName",
      type: ['nonEditableColumn'],
      cellClass: 'stringType',
      width: 200, filterType: 'string'
    },
    {
      headerName: "wcCd", field: "workcenterCd", 
      colId: "workcenter",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellClass: 'stringType',
      valueFormatter: dropdownResourceValueFormatter,
      cellEditorParams: {
        valueFormatter: dropdownResourceValueFormatter,
        values: reduxViewState?.workcenters ?? workcenters
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "wcCd", isDescending),
      width: 200, 
      filterType: 'string',
    },
    { headerName: "resCd", field: "resCd", width: 180, cellClass: 'stringType', filterType: 'string' },
    { headerName: "resName", field: "name", width: 180, filterType: 'string' },
    {
      headerName: "nonOperationCalendar", field: "nopCalGrp", colId: "nopCalGrp", type: "nonEditableColumn",
      valueFormatter,
      cellRenderer: CalendarGroupRenderer, cellRendererParams: { setSnackInfo, calType: "NOP" },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 200, filterType: 'string'
    },
    { headerName: "whpd", field: "workHourPerDay", type: ["rightAligned"], cellDataType: 'number', valueSetter: checkInputValidation, width: 120, filterType: 'number' },
    { headerName: "overWhpd", field: "overWorkHourPerDay", type: ["rightAligned"], cellDataType: 'number', valueSetter: checkInputValidation, width: 140, filterType: 'number' },
    {
      headerName: "workStartDay", field: "workStartDay", colId: "workStartDay",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellClass: 'stringType',
      valueFormatter: workStartDayValueFormatter,
      cellEditorParams: {
        valueFormatter: workStartDayValueFormatter,
        values: weekDays
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "", isDescending),
      width: 120,
      filterType: 'string',
      suppressKeyboardEvent: suppressWorkStartDayKeyDelete
    },
    { headerName: "wdpw", field: "workDayPerWeek", type: ["numberColumn", "rightAligned"], valueSetter: checkInputValidation,
      width: 120, filterType: 'number' },
    { headerName: "resCnt", field: "resCnt", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "descTxt", field: "descTxt", width: 350, filterType: 'string' },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 100, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", valueFormatter: DateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", valueFormatter: DateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];
  
  const retrieveLocation = () => {
    locationService.getAll({ pagination: false })
      .then(response => {
        const { results } = response.data;
        setLocations(results);
        const currentReduxViewState = reduxUtil.getViewState(viewName);
        if (currentReduxViewState) {
          currentReduxViewState.locations = results
          reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...currentReduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveCalendarGroups = () => {
    calendarGroupService.getAll({ searchFilters: { activeFlg: true } })
      .then(response => {
        const { results } = response.data;
        const calender = results.filter((result) => result.calType === "NOP" && result.activeFlg);
        setCalendarGroups(calender);
        const currentReduxViewState = reduxUtil.getViewState(viewName);
        if (currentReduxViewState) {
          currentReduxViewState.calendarGroups = calender
          reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...currentReduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      }); 
  }

  const retrieveWorkcenters = () => {
    workcenterService.getAll()
      .then(response => {
        const { results } = response.data;
        const workcenter = [];
        for (const wc of results) {
          if (!wc.activeFlg) {
            continue;
          }
          workcenter.push(wc?.wcCd)
          workcenterMap.current[wc?.wcCd] = wc;
        }
        setWorkcenters(workcenter);
        const currentReduxViewState = reduxUtil.getViewState(viewName);
        if (currentReduxViewState) {
          currentReduxViewState.workcenters = workcenter
          reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...currentReduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      }); 
  }

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (locationSearch) {
      searchFilters.locCd = locationSearch.locCd;
    }

    if (resourceSearch) {
      searchFilters.resCd = resourceSearch.resCd;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveProductionResources = () => {
    const params = getRequestParams();
    
    resourceService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;

        if (isEmpty(results)) {
          setProductionResources([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setProductionResources(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const currentReduxViewState = reduxUtil.getViewState(viewName);
        if (currentReduxViewState) {
          currentReduxViewState.locationSearch = locationSearch;
          currentReduxViewState.resourceSearch = resourceSearch;
          currentReduxViewState.currentPage = currentPage;
          currentReduxViewState.pageSize = pageSize;
          currentReduxViewState.advancedFilters = advancedFilters;
        }
        reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...currentReduxViewState }));
        DataState.initialize(gridRef.current?.api);
      });
  };

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const removeProductionResource = () => {
    const workcenterIds = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {    
        workcenterIds.push(data.id)  
      }
    );

    if (workcenterIds.length > 0) {
      resourceService.remove(workcenterIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveProductionResources();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }

  }

  const saveProductionResource = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    for (const save of allStateData) {
      if (save.workcenterCd === null) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("workcenter")}] ${t("MSG_FieldRequired")}` });
        return;
      }
      
      if (!save.workStartDay || save.workStartDay === "") {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("workStartDay")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (save?.workcenterCd && workcenterMap.current?.[save.workcenterCd]) {
        save.workcenter = workcenterMap.current[save.workcenterCd];
      }

      if (save.workDayPerWeek === null || save.workDayPerWeek === "") {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("whpd")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      const workDayPerWeekValue = Number(save.workDayPerWeek);

      if (Number.isNaN(workDayPerWeekValue) || !Number.isInteger(workDayPerWeekValue)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("workStartDay")}] ${t("MSG_InvalidValue")}` });
        return false;
      }
    }
    if (allStateData.length > 0) {
      const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};

      const dataWithFlg = allStateData.map((row) => {
        const originalData = updatedOrigin[row.rowIndex];
        let activeFlgChanged = false;
        if (originalData) {
          activeFlgChanged = originalData.activeFlg !== row.activeFlg;
        }

        return { ...row, activeFlgChanged };
      });

      resourceService.upsert(dataWithFlg)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .then(() => {
          retrieveProductionResources();
        });
      

    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const checkActiveFlgChanges = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};

    return allStateData.some((row) => {
      const originalData = updatedOrigin[row.rowIndex];
      if (originalData) {
        return originalData.activeFlg !== row.activeFlg && row.activeFlg === false;
      }
      return false;
    });
  };

  const handleSaveClick = () => {
    gridRef.current?.api?.stopEditing();

    const hasActiveFlgChanges = checkActiveFlgChanges();

    if (hasActiveFlgChanges) {
      setDialogInfo({ open: true, title: "save", content: `${t("MSG_ActiveStatusChangedAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.resource, "deactivate")}` });
    } else {
      setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
    }
  };


  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveProductionResource();
    } else if (action === "DELETE") {
      removeProductionResource();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveProductionResources();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (!isEmpty(locations)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  useEffect(() => {
    if (!isEmpty(calendarGroups)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarGroups]);

  useEffect(() => {
    if (!isUndefined(workcenters) && !isEmpty(workcenters)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workcenters]);

  useEffect(() => {
    retrieveLocation();
    retrieveCalendarGroups();
    retrieveWorkcenters();  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setProductionResources([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearch]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setProductionResources([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceSearch]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveProductionResources();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const addRow = () => {
    const newRow = {
      id: null,
      locCd: null,
      locName: null,
      workcenterCd: null,
      resCd: null,
      name: null,
      nopCalGrpId: null,
      whpd: null,
      overWhpd: null,
      workStartDay: 'mon',
      wdpw: null,
      resCnt: 1,
      activeFlg: true,
      verNum: 0
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch}
          onChange={(_, newValue) => setLocationSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ my: 1, minWidth: 190 }}
          renderInput={(params) => (
            <TextField {...params} label={t("location")} />
          )}
        />
        <Autocomplete
          options={productionResources}
          getOptionLabel={option => option.name}
          value={resourceSearch}
          onChange={(_, newValue) => setResourceSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.resCd === value.resCd}
          size="small"
          sx={{ pl: 1, my: 1, minWidth: 190 }}
          renderInput={(params) => (
            <TextField {...params} label={t("resource")} />
          )}
        />
        <Box sx={{ mt: 1.5, ml: "auto", mr: 1.5 }}>
          <ExcelExportButton title={title} service={resourceService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.resource, 'delete')}` })} />
          <SaveButton onClick={handleSaveClick} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </Box>
      <Box id="production-resource-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={productionResources}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(Resource);